import * as api from '@apis/admin-user-contents';
import * as feedApi from '@apis/app-feed';
import { ReactComponent as StarOn } from '@assets/images/app/star-pink.svg';
import AppPreviewModal from '@components/class-invoice/AppPreviewModal';
import { PRODUCT_ACTIVITY_TYPE_TEXT } from '@models/product';
import { VoteOption, UserContentEditData, UserContentRateType, VoteUserContent } from '@models/user-contents';
import { useLoungeEditorModalStore } from '@stores/loungeEditorModalStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { removeTempProperties } from '@utils/dataTransform';
import { localTimeToUtcISOString, parseMaybeDate, utcTimeToLocalISOString } from '@utils/date';
import { cdnImageUrl, resizeImage } from '@utils/image';
import { extractFileName } from '@utils/string';
import { getUniqueKeyFromUUID } from '@utils/string';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';
import { ColorPicker, DatePicker, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import BottomSheet from './BottomSheet';
import LoungeEditor from './LoungeEditor';
import LoungeEditorTopButton from './LoungeEditorTopButton';
import { Stars } from './Stars';
import { ReactComponent as CheckIcon } from '@assets/images/editor/check-pink.svg';
import BottomRoundButton from '@components/common/BottomRoundButton';
import * as cheerio from 'cheerio';
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const DEFAULT_EDITOR_DATA = {
  rate1: 5,
  rate2: 5,
  rate3: 5,
  rate4: 5,
  text: '',
  originText: '',
  type: 'post',
  category: 'etc',
  board: 'curation',
};
const VOTE_CATEGORY = [
  '먹거리/문화',
  '문화/여가',
  '문화/체험',
  '문화/전시·공연',
  '경제/소비',
  '경제/용돈관리',
  '경제/저축',
  '훈육/생활습관',
  '훈육/예절',
  '훈육/자기관리',
  '교육/학업',
  '교육/독서',
  '교육/놀이학습',
  '교육/사교육',
  '건강/신체',
  '건강/정서',
  '건강/위생',
  '가정/육아',
  '가정/부모교육',
  '여행/국내',
  '여행/해외',
  '디지털/미디어',
  '디지털/습관관리',
  '사회성/관계',
];

const normalizeVoteOptions = (options?: VoteOption[]) => {
  const safeOptions = Array.isArray(options) ? options : [];
  return [...safeOptions]
    .map((opt, index) => ({
      id: opt?.id || getUniqueKeyFromUUID(),
      text: opt?.text || '',
      order: typeof opt?.order === 'number' ? opt.order : index,
      color: opt?.color,
      backgroundColor: opt?.backgroundColor,
    }))
    .sort((a, b) => a.order - b.order)
    .map((opt, index) => ({ ...opt, order: index }));
};

// 편집 중에는 "현재 배열 순서"를 그대로 유지해야 UI에서 즉시 reorder가 반영된다.
// (normalizeVoteOptions는 order 기준으로 다시 정렬하므로 swap이 되돌아갈 수 있음)
const normalizeVoteOptionsKeepArrayOrder = (options?: VoteOption[]) => {
  const safeOptions = Array.isArray(options) ? options : [];
  return [...safeOptions].map((opt, index) => ({
    id: opt?.id || getUniqueKeyFromUUID(),
    text: opt?.text || '',
    order: index,
    color: opt?.color,
    backgroundColor: opt?.backgroundColor,
  }));
};

const SortableVoteOptionItem: React.FC<{
  option: VoteOption;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onChange: (id: string, patch: Partial<VoteOption>) => void;
  onDelete: (id: string) => void;
}> = ({ option, index, isFirst, isLast, onChange, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={{ ...style, display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div
        style={{
          width: '20px',
          textAlign: 'center',
          color: '#9B9DA0',
          fontSize: '12px',
          fontWeight: 600,
          flexShrink: 0,
        }}>
        {index + 1}
      </div>
      <div
        {...listeners}
        {...attributes}
        style={{
          width: '28px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          border: '1px solid #E8EAED',
          cursor: 'grab',
          flexShrink: 0,
          backgroundColor: '#fff',
        }}
        title="드래그해서 순서 변경">
        <DragOutlined style={{ fontSize: '14px', color: '#999' }} />
      </div>
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          border: '1px solid #E8EAED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: option.backgroundColor || '#ffffff',
          color: option.color || '#424242',
          flexShrink: 0,
          fontSize: '12px',
          fontWeight: 700,
        }}
        title="미리보기">
        Aa
      </div>
      <ColorPicker
        value={option.color || '#424242'}
        onChange={(value, hex) => {
          onChange(option.id, { color: hex });
        }}
      />
      <ColorPicker
        value={option.backgroundColor || '#ffffff'}
        onChange={(value, hex) => {
          onChange(option.id, { backgroundColor: hex });
        }}
      />
      <input
        value={option.text}
        placeholder={`옵션 ${index + 1}`}
        onChange={(e) => onChange(option.id, { text: e.target.value })}
        style={{
          width: '100%',
          height: '40px',
          padding: '0 12px',
          borderRadius: '8px',
          border: '1px solid #E8EAED',
          outline: 'none',
          fontSize: '14px',
        }}
      />
      <button
        className="link-button"
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          border: '1px solid #E8EAED',
          fontSize: '12px',
          color: '#424242',
          flexShrink: 0,
          opacity: isFirst && isLast ? 0.6 : 1,
        }}
        onClick={() => onDelete(option.id)}>
        삭제
      </button>
    </div>
  );
};

const createDefaultVote = () => {
  const startDate = moment().toDate();
  const endDate = moment().add(7, 'day').toDate();
  return {
    startDate,
    endDate,
    options: [
      { id: getUniqueKeyFromUUID(), text: '찬성해요', order: 0, color: '#1890FF', backgroundColor: '#E9F3FF' },
      { id: getUniqueKeyFromUUID(), text: '반대해요', order: 1, color: '#FC4242', backgroundColor: '#FEEEEE' },
    ],
  };
};

export const LoungeEditorModal: React.FC = () => {
  const [openChangeCategory, setOpenChangeCategory] = useState<boolean>(false);
  const [openReviewStars, setOpenReviewStars] = useState<boolean>(false);
  const [canPost, setCanPost] = useState<boolean>(false);
  const { show, close, data } = useLoungeEditorModalStore();
  const [editorData, setEditorData] = useState<UserContentEditData>(DEFAULT_EDITOR_DATA);
  const queryClient = useQueryClient();
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }));

  const userContentId = data?.userContentId;
  const paymentStatement = data?.paymentStatement;

  const getUserContentQuery = useQuery({
    queryKey: [`userContent-${userContentId}`],
    queryFn: () => api.getUserContent(userContentId!),
    enabled: !!userContentId,
  });

  const loungeCurationCategoriesQuery = useQuery({
    queryKey: [`lounge-curation-categories`],
    queryFn: () => feedApi.getLoungeCurationCategories(),
  });

  const updateUserContentMutation = useMutation({
    mutationFn: api.updateUserContent,
    onSuccess: async () => {
      setEditorData(DEFAULT_EDITOR_DATA);
      queryClient.invalidateQueries({ queryKey: [`userContent-${userContentId}`] });
      setCanPost(false);
      close();
    },
    onError: () => {
      setCanPost(true);
    },
  });

  const createUserContentMutation = useMutation({
    mutationFn: api.createUserContent,
    onSuccess: async () => {
      setEditorData(DEFAULT_EDITOR_DATA);
      setCanPost(false);
      close();
    },
    onError: () => {
      setCanPost(true);
    },
  });

  const userContent = getUserContentQuery.data?.data;
  const loungeCurationCategories = loungeCurationCategoriesQuery.data?.data?.data || [];
  const loungeCategories = [
    { title: '투표', type: '', board: 'vote' },
    ...loungeCurationCategories.map((item) => ({ ...item, board: 'curation' })),
  ];
  const suggestionHeight = loungeCurationCategories.length * 48 + 68;

  useEffect(() => {
    if (show && userContent) {
      setEditorData(() => {
        const next = { ...userContent, ...userContent?.rates } as UserContentEditData;
        if (next.board === 'vote') {
          next.vote = {
            startDate: parseMaybeDate((next as any)?.vote?.startDate) || createDefaultVote().startDate,
            endDate: parseMaybeDate((next as any)?.vote?.endDate) || createDefaultVote().endDate,
            options: normalizeVoteOptions((next as any)?.vote?.options),
          };
        }
        return next;
      });
    }
  }, [userContent, show]);

  useEffect(() => {
    setEditorData((prev) => {
      const nextBoard = data?.board || prev.board;
      const nextCategory = data?.category || prev.category;
      const next: UserContentEditData = {
        ...prev,
        category: nextCategory,
        board: nextBoard,
      } as UserContentEditData;

      if (nextBoard === 'vote') {
        if (!next.title) next.title = '';
        next.vote = next.vote
          ? {
              startDate: parseMaybeDate((next as any)?.vote?.startDate) || createDefaultVote().startDate,
              endDate: parseMaybeDate((next as any)?.vote?.endDate) || createDefaultVote().endDate,
              options: normalizeVoteOptions((next as any)?.vote?.options),
            }
          : createDefaultVote();
      }

      return {
        ...next,
      } as UserContentEditData;
    });
  }, [data]);

  useEffect(() => {
    const hasOriginText =
      !!editorData?.originText && editorData.originText !== '' && editorData.originText !== '<h1></h1>';

    const baseRequiredOk = hasOriginText;

    const vote = (editorData as any)?.vote;
    const isVote = editorData?.board === 'vote';
    const voteRequiredOk = !isVote
      ? true
      : (() => {
          const titleOk = !!(editorData?.title || '').trim();
          const startDate = parseMaybeDate(vote?.startDate);
          const endDate = parseMaybeDate(vote?.endDate);
          const datesOk = !!startDate && !!endDate && startDate.getTime() < endDate.getTime();

          const options = normalizeVoteOptions(vote?.options);
          const optionsOk = options.length >= 2 && options.every((opt) => !!(opt.text || '').trim());

          return titleOk && datesOk && optionsOk;
        })();

    const toComparable = (value: any) => {
      const cleaned = removeTempProperties(value);
      if (!cleaned) return cleaned;
      const next = { ...cleaned };
      if (next.vote) {
        const safeStart = parseMaybeDate(next.vote.startDate);
        const safeEnd = parseMaybeDate(next.vote.endDate);
        next.vote = {
          ...next.vote,
          startDate: safeStart ? safeStart.toISOString() : null,
          endDate: safeEnd ? safeEnd.toISOString() : null,
          options: normalizeVoteOptions(next.vote.options),
        };
      }
      return next;
    };

    const isDirty = userContentId
      ? JSON.stringify(toComparable(userContent)) !== JSON.stringify(toComparable(editorData))
      : true;

    setCanPost(baseRequiredOk && voteRequiredOk && isDirty);
  }, [editorData, userContent, userContentId]);

  const updateVote = (updater: (prev: VoteUserContent) => VoteUserContent) => {
    setEditorData((prev) => {
      const prevVote = (prev as any).vote || createDefaultVote();
      const nextVote = updater(prevVote);
      return {
        ...prev,
        vote: {
          ...nextVote,
          options: normalizeVoteOptionsKeepArrayOrder(nextVote?.options),
        },
      } as UserContentEditData;
    });
  };

  const voteOptions = normalizeVoteOptionsKeepArrayOrder((editorData as any)?.vote?.options);

  const onVoteOptionsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    updateVote((prevVote) => {
      const options = normalizeVoteOptionsKeepArrayOrder(prevVote?.options);
      const oldIndex = options.findIndex((o: any) => o.id === active.id);
      const newIndex = options.findIndex((o: any) => o.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prevVote;
      return { ...prevVote, options: arrayMove(options, oldIndex, newIndex) };
    });
  };

  const onVoteOptionChange = (
    id: string,
    patch: Partial<{ text: string; color?: string; backgroundColor?: string }>,
  ) => {
    updateVote((prevVote) => ({
      ...prevVote,
      options: normalizeVoteOptionsKeepArrayOrder(prevVote?.options).map((item: VoteOption) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    }));
  };

  const onVoteOptionDelete = (id: string) => {
    updateVote((prevVote) => ({
      ...prevVote,
      options: normalizeVoteOptionsKeepArrayOrder(prevVote?.options).filter((item: VoteOption) => item.id !== id),
    }));
  };

  const onChangeImages = (images: string[], imagesTemp: string[]) => {
    setEditorData((data) => {
      return {
        ...data,
        images: images,
        imagesTemp: imagesTemp,
      };
    });
  };

  const onChangeImageFiles = async (files: FileList) => {
    const imageFiles: any = {};
    const newFiles = await Promise.all(
      Array.from(files).map(async (file) => {
        const result = await resizeImage(file, { maxWidth: 860 });
        const { fileName } = extractFileName(file.name);
        const contentTypes = result.type.split('/');

        const imageFile = new File([result.image], `${fileName}.${contentTypes[1]}`, { type: file.type }) as RcFile;
        const tempUrl = URL.createObjectURL(result.image);
        imageFiles[file.name] = imageFile;

        return { result, fileName: `${fileName}.${contentTypes[1]}`, imageFile, tempUrl };
      }),
    );

    const tempUrls: string[] = [];
    const fileNames: string[] = [];

    newFiles.forEach((newFile) => {
      tempUrls.push(newFile.tempUrl);
      fileNames.push(newFile.fileName);
    });

    setEditorData((data) => {
      return {
        ...data,
        files: { ...data.files, ...imageFiles },
        images: [...(data.images || []), ...fileNames],
        imagesTemp: [...(data.imagesTemp || data.images || []), ...tempUrls],
      };
    });
  };

  const productThumbnail = cdnImageUrl(
    paymentStatement?.product?.thumbnailImage || userContent?.product?.thumbnailImage,
  );
  const productTitle = paymentStatement?.product?.title || userContent?.product?.title;
  const productActivityType = paymentStatement?.product?.activityType || userContent?.product?.activityType;
  const productFavoritedCount = paymentStatement?.product?.favoritedCount || userContent?.product?.favoritedCount;

  return (
    <AppPreviewModal
      key={userContentId}
      open={show}
      title={'글쓰기'}
      leftNaviButtonIcon={'close'}
      onLeftNaviButtonClick={() => {
        setEditorData(DEFAULT_EDITOR_DATA);
        setCanPost(false);
        close();
      }}
      rightNaviButton={
        <div
          style={{
            color: canPost ? '#FF3D8F' : '#B9BBBE',
            fontFamily: 'Pretendard',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 600,
            padding: '0 8px',
          }}
          onClick={() => {
            if (!canPost) {
              return;
            }
            setCanPost(false);
            const formData: FormData = new FormData();
            const json = removeTempProperties(editorData);
            formData.append('json', JSON.stringify(json));

            if (editorData.files) {
              Object.values(editorData.files).forEach((file) => {
                formData.append('files', file);
              });
            }

            if (userContentId) {
              formData.append('userContentId', userContentId);
              updateUserContentMutation.mutate(formData);
            } else {
              createUserContentMutation.mutate(formData);
            }
          }}>
          등록
        </div>
      }>
      <BottomSheet
        hideBackButton
        open={openChangeCategory}
        title={'주제'}
        height={suggestionHeight > 580 ? 580 : suggestionHeight}
        onClose={() => {
          setOpenChangeCategory(false);
        }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {loungeCategories.map((item) => {
            const selected = item.type === editorData.category;
            return (
              <button
                key={`${item.board}-${item.type || 'empty'}`}
                className="link-button"
                style={{
                  padding: '0 20px',
                  color: selected ? '#FF3D8F' : 'var(--Neutral-90, #424242)',
                  fontFamily: 'Pretendard',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  textAlign: 'left',
                  height: '48px',
                }}
                onClick={() => {
                  setEditorData((prev) => {
                    return { ...prev, category: item.type, board: item.board };
                  });
                  setOpenChangeCategory(false);
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>{item.title}</div>
                  <div>{selected && <CheckIcon />}</div>
                </div>
              </button>
            );
          })}
        </div>
      </BottomSheet>
      <BottomSheet
        hideBackButton
        open={openReviewStars}
        title={'후기 작성'}
        height={460}
        onClose={() => {
          setOpenReviewStars(false);
        }}>
        <div>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
              padding: '8px 20px',
              borderTop: '1px solid #E8EAED',
              borderBottom: '1px solid #E8EAED',
            }}>
            <img
              src={productThumbnail}
              alt=""
              style={{
                height: '52px',
                width: '52px',
                objectFit: 'cover',
                borderRadius: '4px',
                display: 'bolck',
                flexShrink: 0,
              }}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minWidth: 0,
                overflow: 'hidden',
              }}>
              <div
                style={{
                  wordBreak: 'break-word',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  color: 'var(--Neutral-90, #424242)',
                  fontFamily: 'Pretendard',
                  fontSize: '13px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                }}>
                {productTitle}
              </div>
              {(productActivityType || productFavoritedCount) && (
                <div
                  style={{
                    display: 'flex',
                    gap: '6px',
                    color: 'var(--Neutral-60, #9B9DA0)',
                    textAlign: 'right',
                    fontFamily: 'Pretendard',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    marginTop: '4px',
                  }}>
                  {productActivityType && <div>{PRODUCT_ACTIVITY_TYPE_TEXT[productActivityType]?.title}</div>}
                  {productActivityType && <div style={{ width: '1px', height: '100%' }}></div>}
                  {productFavoritedCount && productFavoritedCount > 0 && <div>{`찜 ${productFavoritedCount}개`}</div>}
                </div>
              )}
            </div>
          </div>
          <div
            style={{
              color: 'var(--Neutral-100, #242424)',
              textAlign: 'center',
              fontFamily: 'Pretendard',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 700,
              marginTop: '20px',
            }}>
            이 상품 어떠셨나요?
          </div>
          <div style={{ marginTop: '20px' }}>
            {Object.keys(editorData)
              .filter((key) => {
                return key !== 'rates' && key.startsWith('rate');
              })
              .map((key, index) => {
                const rateKeys = [
                  UserContentRateType.SOCIABILITY,
                  UserContentRateType.PROFESSIONALISM,
                  UserContentRateType.READINESS,
                  UserContentRateType.PUNCTUALITY,
                ];
                const value = editorData[key as 'rate1' | 'rate2' | 'rate3' | 'rate4' | 'rate5'];

                return (
                  <div
                    key={key}
                    style={{
                      padding: '0 20px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px',
                    }}>
                    <div
                      style={{
                        color: 'var(--Neutral-90, #424242)',
                        fontFamily: 'Pretendard',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        width: '60px',
                      }}>
                      {rateKeys[index]}
                    </div>
                    <Stars
                      size={24}
                      value={value}
                      onChange={(value: any) => {
                        setEditorData((prev) => {
                          const newValue = { ...prev } as any;
                          newValue[key] = value;
                          return newValue;
                        });
                      }}
                    />
                  </div>
                );
              })}
          </div>
          <div style={{ padding: '12px 20px' }}>
            <BottomRoundButton
              onClick={() => {
                setOpenReviewStars(false);
              }}>
              완료
            </BottomRoundButton>
          </div>
        </div>
      </BottomSheet>
      {(editorData?.board === 'curation' || editorData?.board === 'vote') && (
        <LoungeEditorTopButton
          style={{ borderTop: '1px solid #E8EAED', borderBottom: '1px solid #E8EAED', height: '52px' }}
          onClick={() => {
            setOpenChangeCategory(true);
          }}>
          <div
            style={{
              color: 'var(--Neutral-90, #424242)',
              fontFamily: 'Pretendard',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
            }}>
            {loungeCategories.find((item) => item.type === editorData.category)?.title}
          </div>
        </LoungeEditorTopButton>
      )}
      {editorData?.board === 'vote' && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            padding: '12px 20px',
            borderBottom: '1px solid #E8EAED',
            height: '220px',
          }}>
          <div style={{ display: 'flex' }}>
            <div style={{ flex: 3, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ color: '#424242', fontSize: '13px', fontWeight: 600 }}>투표 기간</div>
              <DatePicker.RangePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                value={
                  (editorData as any)?.vote?.startDate && (editorData as any)?.vote?.endDate
                    ? [
                        dayjs(new Date(utcTimeToLocalISOString((editorData as any)?.vote?.startDate) || '')),
                        dayjs(new Date(utcTimeToLocalISOString((editorData as any)?.vote?.endDate) || '')),
                      ]
                    : null
                }
                onChange={(dates) => {
                  if (!dates || !dates[0] || !dates[1]) return;
                  const startDateIso = localTimeToUtcISOString(dates[0].toDate());
                  const endDateIso = localTimeToUtcISOString(dates[1].toDate());
                  if (!startDateIso || !endDateIso) return;
                  const startDate = new Date(startDateIso);
                  const endDate = new Date(endDateIso);
                  updateVote((prevVote) => ({ ...prevVote, startDate, endDate }));
                }}
                style={{
                  width: '100%',
                  height: '40px',
                }}
              />
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ color: '#424242', fontSize: '13px', fontWeight: 600 }}>투표 카테고리</div>
              <Select
                options={VOTE_CATEGORY.map((value: string) => ({ label: value, value }))}
                value={editorData.category}
                onChange={(value: string) => {
                  setEditorData((prev) => ({ ...prev, category: value }));
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ color: '#424242', fontSize: '13px', fontWeight: 600 }}>투표 옵션 (최소 2개)</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="link-button"
                  style={{
                    height: '28px',
                    padding: '0 10px',
                    borderRadius: '6px',
                    border: '1px solid #E8EAED',
                    fontSize: '12px',
                    color: '#424242',
                  }}
                  onClick={() => {
                    updateVote((prevVote) => {
                      const defaults = createDefaultVote();
                      return {
                        ...prevVote,
                        startDate: prevVote?.startDate || defaults.startDate,
                        endDate: prevVote?.endDate || defaults.endDate,
                        options: defaults.options,
                      };
                    });
                  }}>
                  기본값 추가
                </button>
                <button
                  className="link-button"
                  style={{
                    height: '28px',
                    padding: '0 10px',
                    borderRadius: '6px',
                    border: '1px solid #E8EAED',
                    fontSize: '12px',
                    color: '#424242',
                  }}
                  onClick={() => {
                    updateVote((prevVote) => ({
                      ...prevVote,
                      options: [
                        ...(prevVote?.options || []),
                        {
                          id: getUniqueKeyFromUUID(),
                          text: '',
                          order: (prevVote?.options || []).length,
                          color: '#242424',
                          backgroundColor: '#F5F5F5',
                        },
                      ],
                    }));
                  }}>
                  옵션 추가
                </button>
              </div>
            </div>
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onVoteOptionsDragEnd}>
              <SortableContext items={voteOptions.map((o) => o.id)} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {voteOptions.map((opt: VoteOption, index: number) => (
                    <SortableVoteOptionItem
                      key={opt.id}
                      option={opt}
                      index={index}
                      isFirst={index === 0}
                      isLast={index === voteOptions.length - 1}
                      onChange={onVoteOptionChange}
                      onDelete={onVoteOptionDelete}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}
      {editorData?.board === 'review' && (
        <LoungeEditorTopButton
          style={{
            borderTop: '1px solid #E8EAED',
            borderBottom: '1px solid #E8EAED',
            height: '68px',
          }}
          onClick={() => {
            setOpenReviewStars(true);
          }}>
          <div
            style={{
              height: '52px',
              display: 'flex',
              gap: '12px',
              width: '100%',
              minWidth: 0,
              overflow: 'hidden',
            }}>
            <img
              src={productThumbnail}
              alt=""
              style={{
                height: '52px',
                width: '52px',
                objectFit: 'cover',
                borderRadius: '4px',
                display: 'bolck',
                flexShrink: 0,
              }}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minWidth: 0,
                overflow: 'hidden',
              }}>
              <div
                style={{
                  wordBreak: 'break-word',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  color: 'var(--Neutral-90, #424242)',
                  fontFamily: 'Pretendard',
                  fontSize: '13px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                }}>
                {productTitle}
              </div>
              {(productActivityType || productFavoritedCount) && (
                <div
                  style={{
                    display: 'flex',
                    gap: '6px',
                    color: 'var(--Neutral-60, #9B9DA0)',
                    textAlign: 'right',
                    fontFamily: 'Pretendard',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    marginTop: '4px',
                  }}>
                  {productActivityType && <div>{PRODUCT_ACTIVITY_TYPE_TEXT[productActivityType]?.title}</div>}
                  {productActivityType && <div style={{ width: '1px', height: '100%' }}></div>}
                  {productFavoritedCount && productFavoritedCount > 0 && <div>{`찜 ${productFavoritedCount}개`}</div>}
                </div>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0,
              }}>
              <div>
                <StarOn style={{ width: '24px' }} />
              </div>
              <div
                style={{
                  color: 'var(--Neutral-60, #9B9DA0)',
                  textAlign: 'right',
                  fontFamily: 'Pretendard',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                }}>
                {((editorData.rate1! + editorData.rate2! + editorData.rate3! + editorData.rate4!) / 4).toFixed(1)}
              </div>
            </div>
          </div>
        </LoungeEditorTopButton>
      )}
      <LoungeEditor
        showToolbar
        board={editorData.board}
        value={editorData.originText}
        onChangeImageFiles={onChangeImageFiles}
        onChangeImages={onChangeImages}
        onChange={(html, originHtml) => {
          setEditorData((prev) => {
            const htmlText = html;
            const dom = cheerio.load(htmlText);
            const title = dom('h1').text();
            dom('h1').remove();
            const description = dom('body').text();

            return { ...prev, text: html, originText: originHtml, title, description };
          });
        }}
        attachFilesLimit={5}
        images={editorData.images}
        imagesTemp={editorData.imagesTemp}
      />
    </AppPreviewModal>
  );
};
export default LoungeEditorModal;
